import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import BottomNav from './components/BottomNav';
import Spinner from './components/Spinner';
import Toaster from './components/Toaster';
import Login from './routes/Login';
import Chats from './routes/Chats';
import { SessionProvider } from './state/session';

// The channel view carries the socket, the composer, attachments and the
// receipt sheet — it is by far the largest route and the one you do not need
// on the login screen or the channel list's first paint.
const Channel = lazy(() => import('./routes/Channel'));
const Mentions = lazy(() => import('./routes/Mentions'));
const Profile = lazy(() => import('./routes/Profile'));
const Admin = lazy(() => import('./routes/Admin'));
const ChannelSettings = lazy(() => import('./routes/ChannelSettings'));
const Search = lazy(() => import('./routes/Search'));
const NewChannel = lazy(() => import('./routes/NewChannel'));
const NewDm = lazy(() => import('./routes/NewDm'));
const Diagnostics = lazy(() => import('./routes/Diagnostics'));
const Audit = lazy(() => import('./routes/Audit'));

export default function App() {
  return (
    <SessionProvider
      fallback={(state, retry) => {
        if (state === 'loading') {
          return (
            <div className="flex h-full items-center justify-center">
              <Spinner />
            </div>
          );
        }

        // Signed in, but the phone cannot reach the server. Showing the login
        // screen here would be a lie — and an unusable one, since signing in
        // needs the network too.
        if (state === 'offline') {
          return (
            <div className="flex h-full flex-col items-center justify-center px-8 text-center">
              <img src="/icon.svg" alt="" className="mb-5 h-14 w-14 rounded-2xl opacity-40" />
              <p className="font-semibold text-zinc-900">No connection</p>
              <p className="mt-1 text-sm text-zinc-500">
                You're still signed in. The Huddle will pick up where it left off as soon as you're
                back on wifi or data.
              </p>
              <button
                type="button"
                onClick={retry}
                className="mt-5 rounded-xl bg-brand-600 px-5 py-3 font-semibold text-white active:bg-brand-700"
              >
                Try again
              </button>
            </div>
          );
        }

        return <Login />;
      }}
    >
      <Shell />
    </SessionProvider>
  );
}

function Shell() {
  const location = useLocation();
  // The nav is hidden inside a channel: a messaging app should give the
  // conversation the whole screen, and the back arrow is the way out.
  const showNav = !/^\/(c|settings|search|new)\//.test(location.pathname);

  return (
    <div className="flex h-full flex-col bg-surface">
      {/* Outside the routes, so a message survives the navigation that caused
          it — deleting a channel, for instance, moves you to the chat list. */}
      <Toaster />
      <div className="min-h-0 flex-1">
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center">
              <Spinner />
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<Chats />} />
            <Route path="/c/:channelId" element={<Channel />} />
            <Route path="/settings/:channelId" element={<ChannelSettings />} />
            <Route path="/search/:channelId?" element={<Search />} />
            <Route path="/new/channel" element={<NewChannel />} />
            <Route path="/new/dm" element={<NewDm />} />
            <Route path="/mentions" element={<Mentions />} />
            <Route path="/me" element={<Profile />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/health" element={<Diagnostics />} />
            <Route path="/audit" element={<Audit />} />
            {/* The magic-link callback is served by the Worker, but a stale
                bookmark or a back-button can land here. */}
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </div>
      {showNav && <BottomNav />}
    </div>
  );
}
